import React, { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom";
import { useDispatch } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { BsBookmark } from "react-icons/bs";
import { bulkMoveSavedPostsToFolder, SavedTheVideoInsideFolder, moveVideoToFolder } from "../../../redux/ApiSlice/SavedVideosSlice";
import FolderImage from "../../../assets/icons/common/SavedFolder.png";

const MoveListToFolder = ({
    loading,
    isOpen,
    handleClose,
    SavedFolderList,
    selectedPostId,
    selectedPostIds = [],
    onMoveComplete,
    sourceFolderId
}) => {
    const dispatch = useDispatch();
    const [selectedFolderId, setSelectedFolderId] = useState("");
    const [isMoving, setIsMoving] = useState(false);
    const sheetRef = useRef(null);

    // Reset selected folder when modal opens
    useEffect(() => {
        if (isOpen) {
            setSelectedFolderId("");
        }
    }, [isOpen]);

    // Close when clicking outside the sheet
    useEffect(() => {
        if (!isOpen) return;

        const handleOutsideClick = (event) => {
            if (sheetRef.current && !sheetRef.current.contains(event.target)) {
                handleClose();
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => document.removeEventListener("mousedown", handleOutsideClick);
    }, [isOpen, handleClose]);

    const availableFolders = (SavedFolderList?.data || []).filter(
        (folder) => !sourceFolderId || `${folder.id}` !== `${sourceFolderId}`
    );
    const portalTarget = typeof document !== "undefined" ? document.getElementById("mobile-layout-root") : null;
    const postIdsToMove = selectedPostIds.length > 0
        ? selectedPostIds
        : (selectedPostId ? [selectedPostId] : []);

    const handleMove = async () => {
        if (!selectedFolderId || postIdsToMove.length === 0) return;

        try {
            setIsMoving(true);
            if (postIdsToMove.length > 1) {
                await dispatch(bulkMoveSavedPostsToFolder({
                    folderId: selectedFolderId,
                    postIds: postIdsToMove,
                })).unwrap();
            } else if (sourceFolderId) {
                await dispatch(moveVideoToFolder({
                    sourceFolderId,
                    targetFolderId: selectedFolderId,
                    postId: postIdsToMove[0],
                })).unwrap();
            } else {
                await dispatch(SavedTheVideoInsideFolder({
                    folderId: selectedFolderId,
                    videoId: postIdsToMove[0]
                })).unwrap();
            }

            // Close modal and notify parent component
            handleClose();
            if (onMoveComplete) onMoveComplete();
        } catch (error) {
            console.error("Error moving video:", error);
        } finally {
            setIsMoving(false);
        }
    };

    if (!portalTarget) return null;

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 z-[60] flex items-end bg-black/40"
                    // style={{ paddingBottom: "var(--footer-height, 0px)" }}
                    onClick={handleClose}
                >
                    <motion.div
                        ref={sheetRef}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        initial={{ y: "100%" }}
                        onClick={(e) => e.stopPropagation()}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className="w-full bg-white dark:bg-gray-900 rounded-t-2xl h-[25rem] flex flex-col shadow-md"
                    >
                        {/* Select Folder Section */}
                        <div className="flex flex-col justify-center items-center px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                            <div className="w-16 rounded-full h-[3px] bg-gray-700 mb-2" />
                            <h4 className="text-sm font-semibold text-black dark:text-white text-center">
                                Select Folder
                            </h4>
                        </div>

                        {/* Folders Grid */}
                        <div className="flex-1 overflow-y-scroll px-4 py-4 min-h-0">
                            {availableFolders.length === 0 ? (
                                <div className="flex justify-center items-center h-32">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        No folders available to move to
                                    </p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-2 gap-4">
                                    {availableFolders.map((folder) => (
                                        <div
                                            key={folder.id}
                                            onClick={() => setSelectedFolderId(selectedFolderId === folder.id ? "" : folder.id)}
                                            className={`relative cursor-pointer overflow-hidden ${
                                                selectedFolderId === folder.id
                                                    ? "ring-4 ring-blue-500 ring-offset-2 rounded-md"
                                                    : ""
                                            }`}
                                        >
                                            {/* Folder Thumbnail */}
                                            <div className="relative h-[14rem] bg-gray-200 dark:bg-gray-800">
                                                <img
                                                    src={folder.videos?.[0]?.thumbnail_url || FolderImage}
                                                    alt={folder.name}
                                                    className="w-full h-full object-cover rounded-md"
                                                />

                                                {/* Video Count Badge */}
                                                <div className="absolute top-2 left-2 bg-black bg-opacity-60 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
                                                    <span>{folder.videos?.length || 0}</span>
                                                    <BsBookmark size={10} />
                                                </div>

                                                {/* More Options */}
                                                <button className="absolute top-2 right-2 text-white bg-black bg-opacity-50 rounded-full p-1">
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <circle cx="10" cy="4" r="1.5" />
                                                        <circle cx="10" cy="10" r="1.5" />
                                                        <circle cx="10" cy="16" r="1.5" />
                                                    </svg>
                                                </button>

                                                {/* View Count */}
                                                <div className="absolute bottom-2 left-2 text-white text-[10px] flex items-center gap-1">
                                                    <svg
                                                        className="w-3 h-3"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </div>

                                                {/* Like Count */}
                                                <div className="absolute bottom-2 right-2 text-white text-[10px] flex items-center gap-1">
                                                    <svg
                                                        className="w-3 h-3"
                                                        fill="currentColor"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path
                                                            fillRule="evenodd"
                                                            d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                                                            clipRule="evenodd"
                                                        />
                                                    </svg>
                                                </div>
                                            </div>

                                            {/* Folder Name */}
                                            <div className="flex items-center justify-between">
                                                <p className="text-xs font-semibold text-black dark:text-white truncate">
                                                    {folder.name}
                                                </p>
                                                <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                                    {folder.videos?.length || 0} items
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Action Button */}
                        {selectedFolderId && (
                            <motion.div
                                initial={{ y: 100 }}
                                animate={{ y: 0 }}
                                className="sticky bottom-0 px-4 py-3"
                            >
                                <button
                                    onClick={handleMove}
                                    disabled={!selectedFolderId || loading || isMoving || postIdsToMove.length === 0}
                                    className={`w-full py-3 rounded-lg text-sm font-semibold ${
                                        !selectedFolderId || loading || isMoving || postIdsToMove.length === 0
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            : "bg-[#0A96C1] text-white hover:bg-[#087fa8]"
                                    }`}
                                >
                                    {isMoving ? "Moving..." : `Move ${postIdsToMove.length > 1 ? `${postIdsToMove.length} Videos` : "to Folder"}`}
                                </button>
                            </motion.div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        portalTarget
    );
}

export default MoveListToFolder